import { EventEmitter } from 'events';
import { useAuthStore } from '../store';
import { onOwnAdminHost } from './adminHost';

class WebSocketService extends EventEmitter {
    constructor() {
        super();
        this.ws = null;
        this.reconnectInterval = 5000;
        this.url = '';
        this.isConnected = false;
        this._reconnectTimer = null;
    }

    connect() {
        // REACT_APP_WS_URL when the build has it. Otherwise the same host's /ws,
        // which the ingress routes to the backend — the production image is
        // built without REACT_APP_WS_URL, and <host>:5000 (the old fallback)
        // isn't reachable or allowed by the CSP there, so the header always
        // showed "Offline". Only a local dev server talks to port 5000 directly.
        // On a tenant's own admin subdomain (or super.hotwifi.in) this host's /ws too,
        // as for the API (adminHost.js).
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const { hostname, host } = window.location;
        if (process.env.REACT_APP_WS_URL && !onOwnAdminHost(process.env.REACT_APP_WS_URL)) {
            this.url = process.env.REACT_APP_WS_URL;
        } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
            this.url = `${protocol}//127.0.0.1:5000/ws`;
        } else {
            this.url = `${protocol}//${host}/ws`;
        }

        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }

        console.log(`🔌 Connecting to WebSocket at ${this.url}...`);

        try {
            this.ws = new WebSocket(this.url);
        } catch (err) {
            console.error('❌ WebSocket construction failed:', err);
            this._scheduleReconnect();
            return;
        }

        this.ws.onopen = () => {
            console.log('✅ WebSocket Connected');
            this.isConnected = true;
            // Authenticate with server so updates are scoped to this tenant
            // The access token lives in the auth store's memory, not localStorage.
            const token = useAuthStore.getState().token;
            if (token) {
                try {
                    this.ws.send(JSON.stringify({ type: 'AUTH', token }));
                } catch (_) {}
            }
            this.emit('connected');
        };

        this.ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                this.emit('message', data);
                if (data.type) this.emit(data.type, data);
            } catch {
                console.warn('Invalid WebSocket message:', event.data);
            }
        };

        this.ws.onclose = () => {
            console.warn('⚠️ WebSocket Disconnected');
            this.isConnected = false;
            this.emit('disconnected');
            this._scheduleReconnect();
        };

        this.ws.onerror = (error) => {
            console.error('❌ WebSocket Error:', error);
            if (this.listenerCount('error') > 0) {
                this.emit('error', error);
            }
        };
    }

    _scheduleReconnect() {
        if (this._reconnectTimer) return; // already scheduled
        this._reconnectTimer = setTimeout(() => {
            this._reconnectTimer = null;
            console.log('🔄 Attempting to reconnect WebSocket...');
            this.connect();
        }, this.reconnectInterval);
    }

    send(data) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(data));
        } else {
            console.warn('WebSocket not connected, cannot send:', data);
        }
    }

    disconnect() {
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        if (this.ws) {
            this.ws.onclose = null; // prevent auto-reconnect on manual disconnect
            this.ws.close();
            this.ws = null;
        }
        this.isConnected = false;
    }
}

const wsService = new WebSocketService();
export default wsService;
